import React, { useEffect, useState } from "react";
import {
  Dialog,
  Box,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  Mahnung,
  MahnungStatus,
} from "../../../models/admin-management-entities";
import {
  exportMahnungExcel,
  getMahnungenByStatusList,
} from "../../../api/admin-mahnwesen-api";

interface AdminInkassoDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const AdminInkassoDialog: React.FC<AdminInkassoDialogProps> = ({
  isOpen,
  onClose,
}) => {
  const [mahnungen, setMahnungen] = useState<Mahnung[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen) {
      setSelectedIds([]);
      loadMahnungen();
    }
  }, [isOpen]);

  const loadMahnungen = async () => {
    setIsLoading(true);
    const response = await getMahnungenByStatusList([
      MahnungStatus.ACTIVE,
      MahnungStatus.INKASSO,
    ]);
    setMahnungen(response.mahnungen ?? []);
    setIsLoading(false);
  };

  const toggleSelection = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleExport = async () => {
    if (selectedIds.length > 0) {
      await exportMahnungExcel(selectedIds);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
      <Box
        display="flex"
        flexDirection="column"
        height="80vh" // passt die Höhe des Dialogs an
        padding={3}
      >
        <Typography fontSize={18} fontWeight={600} marginBottom={2}>
          Mahnungen auswählen
        </Typography>

        {isLoading ? (
          <Box display="flex" justifyContent="center" mt={4} flex={1}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            flex={1} // nimmt den verfügbaren Platz
            overflow="auto"
            display="flex"
            flexDirection="column"
            gap={1}
          >
            {mahnungen &&
              mahnungen
                .slice()
                .sort((a, b) => {
                  if (a.IstExportiert !== b.IstExportiert) {
                    return a.IstExportiert ? 1 : -1;
                  }

                  if (!a.IstExportiert && !b.IstExportiert) {
                    if (b.Mahnstufe !== a.Mahnstufe) {
                      return b.Mahnstufe - a.Mahnstufe;
                    }
                  }

                  const dateA = new Date(a.createdDate);
                  const dateB = new Date(b.createdDate);
                  return dateB.getTime() - dateA.getTime();
                })
                .map((m: Mahnung) => {
                  const isSelected =
                    selectedIds && selectedIds.includes(m.ID_Mahnung);

                  return (
                    <Box
                      key={m.ID_Mahnung}
                      onClick={() => toggleSelection(m.ID_Mahnung)}
                      sx={{
                        border: `2px solid ${isSelected ? "#007B55" : "#ccc"}`,
                        borderRadius: "8px",
                        padding: "12px 16px",
                        cursor: "pointer",
                        transition:
                          "border-color 0.2s ease-in-out, box-shadow 0.2s",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        backgroundColor: isSelected ? "#f1fdf7" : "#fff",
                        "&:hover": {
                          borderColor: "#007B55",
                          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                        },
                        marginBottom: "10px",
                      }}
                    >
                      <Typography sx={{ fontSize: "15px" }}>
                        <strong>ID:</strong> {m.ID_Mahnung} | {m.Vorname}{" "}
                        {m.Nachname} | <strong>Stufe:</strong> {m.Mahnstufe}
                      </Typography>

                      {m.IstExportiert && (
                        <Typography
                          sx={{
                            color: "#007B55",
                            fontWeight: "bold",
                            fontSize: "18px",
                          }}
                        >
                          ✓
                        </Typography>
                      )}
                    </Box>
                  );
                })}
          </Box>
        )}

        {/* Fixierter Button */}
        <Box paddingTop={2} borderTop="1px solid #ccc" marginTop={2}>
          <Button
            variant="contained"
            sx={{
              width: "100%",
              height: "40px",
              backgroundColor: "var(--green)",
              color: "white",
              fontWeight: "bold",
              fontSize: "var(--h4)",
              padding: "0",
              "&:hover": {
                backgroundColor: "var(--green)",
                opacity: 0.75,
                transition: "opacity 0.3s",
              },
            }}
            disabled={selectedIds.length === 0}
            onClick={handleExport}
          >
            Excel generieren
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default AdminInkassoDialog;
